import $ from 'jquery';
import Calendar from 'calendar-js';
import Class from './ClassNames';
import { getDayLabelId, makePerformanceListHtml } from './Performance';
import DateFormatter from '../../date/EventListingDateFormatter';
import { makeNoResultsHtml, makeResultsDateRangeHeading } from './../../htmlShared';
import {
  startOfWeek,
  getISODateString,
} from '../../date/DateHelpers';

const calendarJs = new Calendar();

let todayISODateString; // updated every render

/**
 * @param {Map} perfMap
 * @returns {Map}
 */
function filterResults(perfMap) {
  const filteredMap = new Map();

  perfMap.forEach((perfs, key) => {
    // Table view should not show performances that have
    // "Date Displayed" set to false
    const perfsWithDisplayDate = perfs.filter(perf => !!perf.displayDate);
    if (perfsWithDisplayDate.length) {
      filteredMap.set(key, perfsWithDisplayDate);
    }
  });

  return filteredMap;
}

function makeCellHtml(data) {
  const { day, dateKey } = data;
  const classes = [`${Class.BASE}__day-cell`];
  const dayNumberClasses = [`${Class.BASE}__day-number`];
  let ariaAttribute = '';
  const todaySuffix = '--today';

  if (todayISODateString === dateKey) {
    classes.push(`${Class.BASE}__day-cell${todaySuffix}`);
    dayNumberClasses.push(`${Class.BASE}__day-number${todaySuffix}`);
    ariaAttribute = 'aria-current="date"';
  } else {
    if (dateKey < todayISODateString) {
      classes.push(`${Class.BASE}__day-cell--past`);
    }
    if (data.isInPrimaryMonth === false) {
      if (data.isInLastWeekOfPrimaryMonth) {
        classes.push(`${Class.BASE}__day-cell--next-month`);
      } else {
        classes.push(`${Class.BASE}__day-cell--previous-month`);
      }
    }
  }

  return `<td class="${classes.join(' ')}" ${ariaAttribute}>
      <div class="${Class.BASE}__day" data-testid="events-calendar-day-${dateKey}">
        <span class="${dayNumberClasses.join(' ')}" aria-hidden="true">
          ${day}
        </span>
        <span id="${getDayLabelId(dateKey)}" class="sr-only">${DateFormatter.tableCellLabel(data.date)}</span>
      </div>
    </td>`;
}


class EventMonthTable {
  constructor() {
    this.cellsByDate = new Map();
    this.$el = $(`<div class="${Class.BASE}__table-container">`);
    this.resultsFormatter = DateFormatter.month;
    this.thFormatter = DateFormatter.weekdayLabel;
    this.firstDayOfWeek = window.tnew?.app?.calendarViewConfig?.firstDayOfWeek || 0;
  }

  formatDate(date) {
    return this.resultsFormatter(date);
  }

  makeTableHtml(date) {
    const weekStart = startOfWeek(date);
    const year = weekStart.getFullYear();
    const month = weekStart.getMonth();
    const weekdayLabels = [0, 1, 2, 3, 4, 5, 6]
      .map(weekdayIndex => this.thFormatter(new Date(year, month, weekStart.getDate() + weekdayIndex)));
    const weekdayLabelsOrdered = [...weekdayLabels.slice(this.firstDayOfWeek), ...weekdayLabels.slice(0, this.firstDayOfWeek)];

    return `<table class="${Class.BASE}__table">
        <caption>
          ${makeResultsDateRangeHeading(this.resultsFormatter(date))}
        </caption>
        <thead>
          <tr>
            <th>${weekdayLabelsOrdered[0]}</th>
            <th>${weekdayLabelsOrdered[1]}</th>
            <th>${weekdayLabelsOrdered[2]}</th>
            <th>${weekdayLabelsOrdered[3]}</th>
            <th>${weekdayLabelsOrdered[4]}</th>
            <th>${weekdayLabelsOrdered[5]}</th>
            <th>${weekdayLabelsOrdered[6]}</th>
          </tr>
        </thead>
        <tbody></tbody>
      </table>`;
  }

  getWeeksStructure(date) {
    const year = date.getFullYear();
    const month = date.getMonth();

    const detailed = calendarJs.detailed(year, month, (dayData, calendar) => Object.assign({
      dateKey: getISODateString(dayData.date),
    }, dayData));

    const detailedCalendar = detailed.calendar.map(week => {
      return [...week.slice(this.firstDayOfWeek), ...week.slice(0, this.firstDayOfWeek)];
    });

    return detailedCalendar;
  }

  setupView(date) {
    const weeks = this.getWeeksStructure(date);

    const $tableContents = $(this.makeTableHtml(date));
    const $tbody = $tableContents.find('tbody');

    this.cellsByDate.clear();

    weeks.forEach((week) => {
      const cells = [];

      week.forEach((day) => {
        const $cell = $(makeCellHtml(day));

        this.cellsByDate.set(day.dateKey, $cell);

        cells.push($cell);
      });

      const $row = $('<tr>');
      $row.append(cells);

      $tbody.append($row);
    });

    this.$el.append($tableContents);
  }

  clearResults() {
    this.$el.html('');
  }

  /**
   * @param {Map} perfMap
   * @param {Date} date
   */
  renderResults(perfMap, date) {
    this.$el.empty();
    todayISODateString = getISODateString(new Date());
    // todayISODateString = '2020-03-17';

    const filteredPerfMap = filterResults(perfMap);

    if (filteredPerfMap.size > 0) {
      this.setupView(date);

      filteredPerfMap.forEach((dayPerfs, key) => {
        const $cell = this.cellsByDate.get(key);
        if ($cell) {
          const listHtml = makePerformanceListHtml(dayPerfs, key);
          $cell.find(`.${Class.BASE}__day`).append(listHtml);
        }
      });
    } else {
      const noResultsHtml = makeNoResultsHtml(this.resultsFormatter(date));
      this.$el.append(noResultsHtml);
    }
  }
}

export default EventMonthTable;
